import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { LockIcon, MailIcon, AlertCircleIcon, Eye, EyeOff } from 'lucide-react';
export const TeacherLogin = () => {
  const [email, setEmail] = useState('');
  const [passport, setPassport] = useState('');
  const [showPassport, setShowPassport] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const {
    login,
    currentUser
  } = useAuth();
  const navigate = useNavigate();
  useEffect(() => {
    // Check if user just verified email and should auto-redirect
    const emailJustVerified = localStorage.getItem('emailJustVerified');
    const verificationTimestamp = localStorage.getItem('verificationTimestamp');
    if (emailJustVerified === 'true' && verificationTimestamp) {
      const timestamp = parseInt(verificationTimestamp, 10);
      const now = Date.now();
      const fiveMinutes = 5 * 60 * 1000;
      // If verification happened within last 5 minutes
      if (now - timestamp < fiveMinutes) {
        console.log('[TeacherLogin] Email just verified, checking auth state');
        // Check if user is already logged in
        if (currentUser && currentUser.emailVerified) {
          console.log('[TeacherLogin] User verified and logged in, redirecting to dashboard');
          // Clear flags
          localStorage.removeItem('emailJustVerified');
          localStorage.removeItem('verificationTimestamp');
          // Redirect to dashboard
          navigate('/teacher-dashboard');
        }
      } else {
        // Clear expired flags
        localStorage.removeItem('emailJustVerified');
        localStorage.removeItem('verificationTimestamp');
      }
    }
    // Check for verified query parameter
    const params = new URLSearchParams(window.location.search);
    if (params.get('verified') === 'true') {
      setMessage('Email verified successfully! Please log in to continue.');
    }
  }, [currentUser, navigate]);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!email || !passport) {
      setError('Please enter both email and passport');
      return;
    }
    try {
      setIsLoading(true);
      console.log('=== TEACHER LOGIN ATTEMPT ===');
      console.log('Email:', email);
      console.log('Calling login with type: teacher');
      // FIX: Add timeout protection (15 seconds)
      const loginPromise = login(email, passport, 'teacher');
      const timeoutPromise = new Promise((_, reject) => setTimeout(() => reject(new Error('Login timeout - please try again')), 15000));
      await Promise.race([loginPromise, timeoutPromise]);
      console.log('Login successful, navigating to dashboard');
      navigate('/teacher-dashboard');
    } catch (err: any) {
      console.error('=== TEACHER LOGIN ERROR ===');
      console.error('Login error:', err);
      console.error('Error message:', err.message);
      console.error('Error code:', err.code);
      // Handle timeout specifically (user-actionable)
      if (err.message?.includes('timeout')) {
        setError('Login is taking too long. Please check your internet connection and try again.');
      } else if (err.code === 'auth/too-many-requests') {
        setError('Too many failed login attempts. Please try again later.');
      } else if (err.code === 'auth/network-request-failed') {
        setError('Network error. Please check your internet connection and try again.');
      } else {
        // Generic message for all auth failures (security best practice)
        setError('Invalid email or password');
      }
    } finally {
      setIsLoading(false);
    }
  };
  return <div className="min-h-screen bg-gradient-to-r from-[#0F1626] via-[#1E3A8A] to-[#2C3E50] flex items-center justify-center px-4 py-12" data-id="element-8609">
      <div className="max-w-md w-full bg-white rounded-xl shadow-xl overflow-hidden" data-id="element-8610">
        <div className="px-8 py-10" data-id="element-8611">
          <div className="text-center mb-10" data-id="element-8612">
            <h2 className="text-3xl font-bold text-navy-900 mb-2" data-id="element-8613">
              Teacher Login
            </h2>
            <p className="text-gray-600" data-id="element-8614">Sign in to access your dashboard</p>
          </div>

          {error && <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 text-red-700 flex items-start" data-id="element-8615">
              <AlertCircleIcon className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" data-id="element-8616" />
              <span data-id="element-8617">{error}</span>
            </div>}

          {message && <div className="mb-6 p-4 bg-green-50 border-l-4 border-green-500 text-green-700 flex items-start" data-id="element-8618">
              <AlertCircleIcon className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" data-id="element-8619" />
              <span data-id="element-8620">{message}</span>
            </div>}

          <form onSubmit={handleSubmit} className="space-y-6" data-id="element-8621">
            <div data-id="element-8622">
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-8623">
                Email Address
              </label>
              <div className="relative" data-id="element-8624">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-8625">
                  <MailIcon className="h-5 w-5 text-gray-400" data-id="element-8626" />
                </div>
                <input id="email" type="email" value={email} onChange={e => setEmail(e.target.value)} className="pl-10 w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-deep-pink focus:border-deep-pink text-gray-900" placeholder="you@example.com" required disabled={isLoading} data-id="element-8627" />
              </div>
            </div>

            <div data-id="element-8628">
              <div className="flex items-center justify-between mb-1" data-id="element-8629">
                <label htmlFor="passport" className="block text-sm font-medium text-gray-700" data-id="element-8630">
                  Passport
                </label>
                <Link to="/forgot-password" className="text-sm text-deep-pink hover:text-burgundy" data-id="element-8631">
                  Forgot password?
                </Link>
              </div>
              <div className="relative" data-id="element-8632">
                <input type={showPassport ? 'text' : 'password'} id="passport" value={passport} onChange={e => setPassport(e.target.value)} className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:ring-deep-pink focus:border-deep-pink text-gray-900" placeholder="Enter your passport" required disabled={isLoading} data-id="element-8633" />
                <button type="button" onClick={() => setShowPassport(!showPassport)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none" aria-label={showPassport ? 'Hide passport' : 'Show passport'} data-id="element-8634">
                  {showPassport ? <EyeOff size={20} data-id="element-8635" /> : <Eye size={20} data-id="element-8636" />}
                </button>
              </div>
            </div>

            <div className="flex items-center" data-id="element-8637">
              <input id="remember-me" name="remember-me" type="checkbox" checked={rememberMe} onChange={() => setRememberMe(!rememberMe)} className="h-4 w-4 text-deep-pink focus:ring-deep-pink border-gray-300 rounded" data-id="element-8638" />
              <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700" data-id="element-8639">
                Remember me
              </label>
            </div>

            <button type="submit" disabled={isLoading} className="w-full bg-deep-pink hover:bg-burgundy text-white font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center" data-id="element-8640">
              {isLoading ? <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-8641">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-8642"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-8643"></path>
                  </svg>
                  Signing In...
                </> : 'Sign In'}
            </button>
          </form>

          <div className="mt-8 text-center" data-id="element-8644">
            <p className="text-gray-600" data-id="element-8645">
              Don't have an account?{' '}
              <Link to="/teacher-signup" className="text-deep-pink hover:text-burgundy font-medium" data-id="element-8646">
                Sign up
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>;
};